/**
 * V60.2: OrgTransferPage - 조직원 전배 페이지 (2-Tab 구조)
 *
 * 탭1: 전배요청 - 다른 조직 직원을 내 조직으로 전배 요청
 * 탭2: 전배승인 - 타팀이 내 팀 직원에게 보낸 전배 요청 승인/반려
 *
 * approvalRequests(type='transfer') 단일 SSoT (V60.1).
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Search,
  Calendar,
  AlertCircle,
  CheckCircle,
  X,
  UserCheck,
  XCircle,
  Send,
  ClipboardCheck,
} from 'lucide-react';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { useOrgManagementScope } from '../../shared/hooks/useOrgManagementScope';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { useTransferRequestMutations } from '../../shared/hooks/useTransferRequestMutations';
import {
  useTransferRequests,
  TRANSFER_STATUS_LABELS,
  TRANSFER_STATUS_COLORS,
} from '../../shared/hooks/useTransferRequests';
import { Employee } from '../../shared/types/employee';
import { ApprovalRequest } from '../../shared/types/approvalRequest';
import { formatTransferEffectiveDate } from '../../shared/utils/dateUtils';  // V66: 전배일(실제 진행/예정일) 공용 포맷터

type TabType = 'request' | 'approval';

export function OrgTransferPage() {
  const authContext = useAuthContext();
  const { isLoading: scopeLoading } = useOrgManagementScope();
  const { employees, isLoading: employeesLoading } = useEmployees({ forTransfer: true });
  const { orgUnits } = useOrgUnitsPublic();
  const {
    createRequest,
    approveByFromOrg,
    reject,
    isSubmitting,
    error: mutationError,
  } = useTransferRequestMutations();

  const [activeTab, setActiveTab] = useState<TabType>('request');

  // ─── Tab1: 전배요청 (내가 보낸 요청 이력: requesterId = 나) ───
  const { transferRequests: sentRequests, isLoading: sentLoading } = useTransferRequests({
    requesterId: authContext.userId || undefined,
  });

  // ─── Tab2: 전배승인 (내가 승인자인 전배 요청) ───
  const { transferRequests: approvalRequests, isLoading: approvalLoading, refetch: refetchApprovals } = useTransferRequests({
    approverId: authContext.userId || undefined,
  });

  // Tab2: pending 건 분리
  const pendingApprovals = useMemo(
    () => approvalRequests.filter(r => r.status === 'pending'),
    [approvalRequests]
  );
  const completedApprovals = useMemo(
    () => approvalRequests.filter(r => r.status !== 'pending'),
    [approvalRequests]
  );

  // ─── Tab1: 폼 상태 ───
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [searchText, setSearchText] = useState('');
  const [transferMode, setTransferMode] = useState<'immediate' | 'scheduled'>('immediate');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formError, setFormError] = useState('');

  // 현재 사용자의 조직 정보
  const myOrgUnit = useMemo(() => {
    if (!authContext.orgUnitId || !orgUnits.length) return null;
    return orgUnits.find(o => o.id === authContext.orgUnitId) || null;
  }, [authContext.orgUnitId, orgUnits]);

  // 선택된 직원의 조직 정보
  const fromOrgUnit = useMemo(() => {
    if (!selectedEmployee?.orgUnitId || !orgUnits.length) return null;
    return orgUnits.find(o => o.id === selectedEmployee.orgUnitId) || null;
  }, [selectedEmployee, orgUnits]);

  // 검색 필터링: 현재 사용자와 다른 조직 소속 직원만
  const filteredEmployees = useMemo(() => {
    if (!authContext.orgUnitId) return [];
    return employees.filter(emp => {
      if (emp.orgUnitId === authContext.orgUnitId) return false;
      if (searchText) {
        const s = searchText.toLowerCase();
        const name = (emp.nameKo || '').toLowerCase();
        const employeeNumber = (emp.employeeNumber || '').toLowerCase();
        return name.includes(s) || employeeNumber.includes(s);
      }
      return true;
    });
  }, [employees, authContext.orgUnitId, searchText]);

  // Tab1: 제출 핸들러
  const handleSubmit = useCallback(async () => {
    if (!selectedEmployee || !authContext.orgUnitId) return;

    setFormError('');
    setSuccessMessage('');

    if (!reason.trim()) {
      setFormError('전배 사유를 입력해주세요.');
      return;
    }

    if (transferMode === 'scheduled' && !effectiveDate) {
      setFormError('예약 전배 시 시행일을 선택해주세요.');
      return;
    }

    try {
      await createRequest({
        employeeId: selectedEmployee.id,
        fromOrgUnitId: selectedEmployee.orgUnitId || '',
        toOrgUnitId: authContext.orgUnitId,
        reason: reason.trim(),
        transferMode,
        effectiveDate: transferMode === 'scheduled' ? effectiveDate : undefined,
      });

      setSuccessMessage('전배 요청이 생성되었습니다. 보내는 조직장의 승인을 기다립니다.');
      setSelectedEmployee(null);
      setSearchText('');
      setReason('');
      setEffectiveDate('');
      setTransferMode('immediate');
    } catch (err: any) {
      setFormError(err?.message || '전배 요청 생성 실패');
    }
  }, [selectedEmployee, authContext.orgUnitId, reason, transferMode, effectiveDate, createRequest]);

  // Tab2: 승인 핸들러
  const handleApprove = useCallback(async (req: ApprovalRequest) => {
    const rd = req.requestData || {};
    if (!window.confirm(`${rd.employeeName || '직원'}의 전배 요청을 승인하시겠습니까?`)) return;

    try {
      setFormError('');
      await approveByFromOrg(req.id);
      setSuccessMessage('전배 요청을 승인했습니다. 인사팀 승인을 기다립니다.');
      refetchApprovals();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err: any) {
      setFormError(err?.message || '승인 처리 실패');
    }
  }, [approveByFromOrg, refetchApprovals]);

  // Tab2: 반려 핸들러
  const handleReject = useCallback(async (req: ApprovalRequest) => {
    const comment = prompt('반려 사유를 입력해주세요:');
    if (!comment) return;

    try {
      setFormError('');
      await reject(req.id, comment);
      setSuccessMessage('전배 요청을 반려했습니다.');
      refetchApprovals();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setFormError(err?.message || '반려 처리 실패');
    }
  }, [reject, refetchApprovals]);

  // 공용: 상태 뱃지 스타일
  const getStatusStyle = (status: string) => {
    const c = TRANSFER_STATUS_COLORS[status] || 'gray';
    const colorMap: Record<string, { color: string; bg: string }> = {
      blue: { color: '#2563eb', bg: '#eff6ff' },
      purple: { color: '#7c3aed', bg: '#f3e8ff' },
      green: { color: '#16a34a', bg: '#f0fdf4' },
      orange: { color: '#d97706', bg: '#fffbeb' },
      red: { color: '#dc2626', bg: '#fef2f2' },
      gray: { color: '#6b7280', bg: '#f9fafb' },
    };
    return colorMap[c] || colorMap.gray;
  };

  // V66 RCA: 로컬 formatDate 제거 (callable 응답 Timestamp 가 plain object `{_seconds, _nanoseconds}` 로
  // 와서 `new Date(obj)` 가 Invalid Date 반환 → "Invalid Date" 노출). 공용 formatDateByCompanyCode 사용.

  if (scopeLoading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        로딩 중...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>조직원 전배</h2>
        {/* V68: 페이지 가이드 안내 */}
        <div style={{ marginTop: 8, color: '#94a3b8', fontSize: '13px', lineHeight: 1.7 }}>
          <p style={{ margin: 0 }}>· 조직원 전배는 전배를 받을 조직장이 신청 후, 전배를 보내는 조직장의 승인과 최종 인사팀 승인을 거쳐 진행됩니다.</p>
          <p style={{ margin: 0 }}>· 전배는 즉시 진행하거나 예약으로 진행할 수 있습니다.</p>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div style={{
        display: 'flex',
        gap: 0,
        borderBottom: '2px solid #e5e7eb',
        marginBottom: 24,
      }}>
        <button
          onClick={() => setActiveTab('request')}
          style={{
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: activeTab === 'request' ? 600 : 400,
            color: activeTab === 'request' ? '#2563eb' : '#6b7280',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'request' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: -2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Send size={15} />
          전배요청
        </button>
        <button
          onClick={() => setActiveTab('approval')}
          style={{
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: activeTab === 'approval' ? 600 : 400,
            color: activeTab === 'approval' ? '#2563eb' : '#6b7280',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'approval' ? '2px solid #2563eb' : '2px solid transparent',
            marginBottom: -2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <ClipboardCheck size={15} />
          전배승인
          {pendingApprovals.length > 0 && (
            <span style={{
              background: '#dc2626',
              color: 'white',
              borderRadius: 10,
              padding: '1px 7px',
              fontSize: 11,
              fontWeight: 600,
              minWidth: 18,
              textAlign: 'center',
            }}>
              {pendingApprovals.length}
            </span>
          )}
        </button>
      </div>

      {/* 성공/에러 메시지 (공용) */}
      {successMessage && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 8, color: '#16a34a', fontSize: 13, marginBottom: 16,
        }}>
          <CheckCircle size={16} />
          {successMessage}
        </div>
      )}
      {(formError || mutationError) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16,
        }}>
          <AlertCircle size={16} />
          {formError || mutationError}
        </div>
      )}

      {/* ═══ Tab1: 전배요청 ═══ */}
      {activeTab === 'request' && (
        <>
          {/* 전배 요청 폼 */}
          <div style={{
            background: 'white', borderRadius: 12, border: '1px solid #e5e7eb',
            padding: 24, marginBottom: 32,
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 600 }}>전배 요청</h3>

            {/* 받는 조직 (자동) */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                받는 조직 (내 조직)
              </label>
              <div style={{
                padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 8, color: '#374151', fontSize: 14,
              }}>
                {myOrgUnit?.name || '-'}
              </div>
            </div>

            {/* 대상 직원 선택 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                대상 직원 (다른 조직 소속)
              </label>

              {selectedEmployee ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                }}>
                  <span style={{ fontWeight: 500, color: '#1d4ed8' }}>
                    {selectedEmployee.nameKo} ({selectedEmployee.employeeNumber})
                  </span>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>
                    - {fromOrgUnit?.name || '-'}
                  </span>
                  <button
                    onClick={() => setSelectedEmployee(null)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: '#9ca3af' }} />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="이름 또는 사번으로 검색..."
                      style={{
                        width: '100%', padding: '10px 14px 10px 36px', border: '1px solid #d1d5db',
                        borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  {searchText && (
                    <div style={{
                      maxHeight: 240, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white',
                    }}>
                      {employeesLoading ? (
                        <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>로딩 중...</div>
                      ) : filteredEmployees.length === 0 ? (
                        <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>검색 결과가 없습니다.</div>
                      ) : (
                        filteredEmployees.slice(0, 20).map(emp => {
                          const empOrg = orgUnits.find(o => o.id === emp.orgUnitId);
                          return (
                            <div
                              key={emp.id}
                              onClick={() => { setSelectedEmployee(emp); setSearchText(''); }}
                              style={{
                                padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                            >
                              <span style={{ fontWeight: 500, fontSize: 14 }}>{emp.nameKo} ({emp.employeeNumber})</span>
                              <span style={{ color: '#6b7280', fontSize: 13 }}>{empOrg?.name || '-'}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 보내는 조직 (자동) */}
            {selectedEmployee && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>보내는 조직</label>
                <div style={{
                  padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: 8, color: '#374151', fontSize: 14,
                }}>
                  {fromOrgUnit?.name || '-'}
                </div>
              </div>
            )}

            {/* 전배 모드 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>전배 모드</label>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  border: `2px solid ${transferMode === 'immediate' ? '#2563eb' : '#d1d5db'}`,
                  borderRadius: 8, cursor: 'pointer',
                  background: transferMode === 'immediate' ? '#eff6ff' : 'white', fontSize: 14,
                }}>
                  <input type="radio" name="transferMode" value="immediate" checked={transferMode === 'immediate'}
                    onChange={() => setTransferMode('immediate')} style={{ display: 'none' }} />
                  즉시 전배
                </label>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  border: `2px solid ${transferMode === 'scheduled' ? '#2563eb' : '#d1d5db'}`,
                  borderRadius: 8, cursor: 'pointer',
                  background: transferMode === 'scheduled' ? '#eff6ff' : 'white', fontSize: 14,
                }}>
                  <input type="radio" name="transferMode" value="scheduled" checked={transferMode === 'scheduled'}
                    onChange={() => setTransferMode('scheduled')} style={{ display: 'none' }} />
                  <Calendar size={14} />
                  예약 전배
                </label>
              </div>
            </div>

            {/* 예약일 */}
            {transferMode === 'scheduled' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>시행일</label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                  style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none' }}
                />
              </div>
            )}

            {/* 사유 */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>전배 사유 *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="전배 사유를 입력해주세요."
                rows={3}
                style={{
                  width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* 제출 버튼 */}
            <button
              onClick={handleSubmit}
              disabled={!selectedEmployee || isSubmitting}
              style={{
                padding: '10px 24px',
                background: !selectedEmployee || isSubmitting ? '#d1d5db' : '#2563eb',
                color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500,
                cursor: !selectedEmployee || isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? '요청 중...' : '전배 요청'}
            </button>
          </div>

          {/* 내 전배 요청 이력 */}
          <div style={{
            background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24,
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>내 전배 요청 이력</h3>

            {sentLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>로딩 중...</div>
            ) : sentRequests.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>전배 요청 이력이 없습니다.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>대상 직원</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>보내는 조직</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>받는 조직</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>모드</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>상태</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>전배 사유</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>반려 사유</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>전배일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentRequests.map(req => {
                      const rd = req.requestData || {};
                      const fromOrgId = req.transferFromOrgUnitId || rd.fromOrgUnitId;
                      const toOrgId = req.transferToOrgUnitId || rd.toOrgUnitId;
                      const fromOrg = orgUnits.find(o => o.id === fromOrgId);
                      const toOrg = orgUnits.find(o => o.id === toOrgId);
                      const empId = req.transferTargetUserId || rd.targetUserId || rd.employeeId;
                      const emp = employees.find(e => e.id === empId);
                      const mode = req.transferMode || rd.transferMode;
                      const effDate = req.transferEffectiveDate || rd.effectiveDate;
                      const ss = getStatusStyle(req.status);
                      return (
                        <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px' }}>{rd.employeeName || emp?.nameKo || empId}</td>
                          <td style={{ padding: '10px 12px' }}>{rd.fromOrgUnitName || fromOrg?.name || fromOrgId}</td>
                          <td style={{ padding: '10px 12px' }}>{rd.toOrgUnitName || toOrg?.name || toOrgId}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {mode === 'scheduled' ? (
                              <span style={{ color: '#d97706', fontSize: 13 }}>예약 ({effDate})</span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: 13 }}>즉시</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                              fontSize: 12, fontWeight: 500, color: ss.color, background: ss.bg,
                            }}>
                              {TRANSFER_STATUS_LABELS[req.status] || req.status}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {rd.reason || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: req.status === 'rejected' ? '#dc2626' : '#9ca3af' }}>
                            {req.status === 'rejected' ? (req.comment || '-') : '-'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                            {formatTransferEffectiveDate(req)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══ Tab2: 전배승인 ═══ */}
      {activeTab === 'approval' && (
        <>
          {/* 승인대기 목록 */}
          <div style={{
            background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, marginBottom: 32,
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              승인대기
              {pendingApprovals.length > 0 && (
                <span style={{
                  background: '#dc2626', color: 'white', borderRadius: 10,
                  padding: '1px 8px', fontSize: 12, fontWeight: 600,
                }}>
                  {pendingApprovals.length}
                </span>
              )}
            </h3>

            {approvalLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>로딩 중...</div>
            ) : pendingApprovals.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                승인 대기 중인 전배 요청이 없습니다.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>대상 직원</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>보내는 조직</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>요청자 (받는 조직)</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>모드</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>전배 사유</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>전배일</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingApprovals.map(req => {
                      const rd = req.requestData || {};
                      const mode = req.transferMode || rd.transferMode;
                      const effDate = req.transferEffectiveDate || rd.effectiveDate;
                      return (
                        <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                            {rd.employeeName || req.transferTargetUserId || '-'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            {rd.fromOrgUnitName || req.transferFromOrgUnitId || '-'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div>
                              <span style={{ fontWeight: 500 }}>{req.requesterName || req.requesterId}</span>
                              <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 4 }}>
                                ({rd.toOrgUnitName || req.requesterOrgUnitName || '-'})
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {mode === 'scheduled' ? (
                              <span style={{ color: '#d97706', fontSize: 13 }}>예약 ({effDate})</span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: 13 }}>즉시</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {rd.reason || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                            {formatTransferEffectiveDate(req)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                              <button
                                onClick={() => handleApprove(req)}
                                disabled={isSubmitting}
                                title="승인"
                                style={{
                                  padding: '6px 12px', background: '#2563eb', color: 'white',
                                  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  opacity: isSubmitting ? 0.5 : 1,
                                }}
                              >
                                <UserCheck size={14} />
                                승인
                              </button>
                              <button
                                onClick={() => handleReject(req)}
                                disabled={isSubmitting}
                                title="반려"
                                style={{
                                  padding: '6px 12px', background: 'white', color: '#dc2626',
                                  border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, fontWeight: 500,
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  opacity: isSubmitting ? 0.5 : 1,
                                }}
                              >
                                <XCircle size={14} />
                                반려
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 처리 완료 이력 */}
          {completedApprovals.length > 0 && (
            <div style={{
              background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24,
            }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: '#6b7280' }}>처리 완료</h3>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>대상 직원</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>조직 이동</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>상태</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>전배 사유</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>반려 사유</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>전배일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedApprovals.map(req => {
                      const rd = req.requestData || {};
                      const ss = getStatusStyle(req.status);
                      return (
                        <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px' }}>
                            {rd.employeeName || req.transferTargetUserId || '-'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ color: '#6b7280' }}>{rd.fromOrgUnitName || req.transferFromOrgUnitId || '-'}</span>
                            <span style={{ margin: '0 6px', color: '#d1d5db' }}>&rarr;</span>
                            <span style={{ color: '#2563eb', fontWeight: 500 }}>{rd.toOrgUnitName || req.transferToOrgUnitId || '-'}</span>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                              fontSize: 12, fontWeight: 500, color: ss.color, background: ss.bg,
                            }}>
                              {TRANSFER_STATUS_LABELS[req.status] || req.status}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {rd.reason || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: req.status === 'rejected' ? '#dc2626' : '#9ca3af' }}>
                            {req.status === 'rejected' ? (req.comment || '-') : '-'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                            {formatTransferEffectiveDate(req)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
